import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { auth } from './Firebase';
import { Link as RouterLink } from 'react-router-dom';
import { slugify } from '../utils/slugify';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, TableSortLabel } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Helmet } from 'react-helmet-async';
import CustomSelect from './CustomSelect';

// Bezpieczna pomocnicza funkcja do formatowania daty, identyczna jak w WcPositionMatrix.js
const convertUTCToLocal = (utcValue) => {
  if (!utcValue) return '';
  let utcDate;
  if (typeof utcValue === 'number') {
    utcDate = new Date(utcValue);
  } else if (typeof utcValue === 'string') {
    utcDate = new Date(utcValue.replace(' ', 'T') + 'Z');
  } else {
    return '';
  }
  return utcDate.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' });
};

// Oryginalny gradient i cieniowanie z pliku wzorcowego
const getGradientColor = (value) => {
  if (value === 0) return 'rgba(255, 255, 255, 0.3)'; // Pojaśniony neutralny szary
  const scaledValue = Math.max(0, value / 0.9);
  const grayBase = 255;
  const r = Math.round(grayBase * (1 - scaledValue));
  const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
  const b = Math.round(grayBase * (1 - scaledValue) + 50 * scaledValue); // Lekkie niebieskie dla głębi
  const alpha = 0.3 + scaledValue * 0.7;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatChance = (chance, useTilde = false) => {
  if (typeof chance !== 'number' || isNaN(chance)) return '—';

  const percent = chance * 100;

  if (percent < 0.001) return useTilde ? '~0.0%' : '0.0%';
  if (percent > 99.999) return useTilde ? '~100%' : '100.0%';

  const decimals = ((percent < 0.1 && percent > 0) || (percent > 99.9 && percent < 100)) ? 2 : 1;
  const formatted = percent.toFixed(decimals);

  return useTilde && (percent < 0.01 || percent > 99.99)
    ? `~${formatted}%`
    : `${formatted}%`;
};

const WcSummary = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('gold');
  const [order, setOrder] = useState('desc');
  const [simulations, setSimulations] = useState([]);
  const [selectedSimulationId, setSelectedSimulationId] = useState('');
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [secondSimulationId, setSecondSimulationId] = useState('');
  const [secondSimData, setSecondSimData] = useState([]); // Przechowuje surowe dane drugiej symulacji

  const simulationOptions = simulations.map((sim) => ({
    value: sim.simulation_id,
    label: sim.displayName
  }));

  // 1. Pobieranie listy symulacji z filtrowaniem po użytkowniku (identycznie jak w WcPositionMatrix)
  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/api/competition/431/simulations`
      )
      .then((response) => {
        const sims = response.data || [];

        // Mapowanie danych (dodanie userId i gwiazdki dla symulacji warunkowych)
        const simList = sims.map(sim => ({
          ...sim,
          userId: sim.user_id, 
          displayName: sim.conditions && sim.conditions !== "" 
            ? `${convertUTCToLocal(sim.created_at)} (*)` 
            : convertUTCToLocal(sim.created_at)
        }));

        // Filtrowanie symulacji (domyślne oraz należące do zalogowanego użytkownika)
        const filteredSimList = simList.filter(sim => {
          const isDefault = !sim.userId || sim.userId === ""; 
          const isOwned = auth.currentUser?.uid && sim.userId === auth.currentUser.uid;
          return isDefault || isOwned;
        });

        setSimulations(filteredSimList);

        if (filteredSimList.length > 0) {
          setSelectedSimulationId(filteredSimList[0].simulation_id);
        }
      })
      .catch((err) => {
        console.error('Błąd pobierania listy symulacji:', err);
      });
  }, [auth.currentUser]);

  // 2. Pobieranie szczegółowych danych podsumowania dla konkretnej symulacji
  useEffect(() => {
    if (!selectedSimulationId) return;

    setLoading(true);

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/api/simulation/worldcup/latest-stages?simulationId=${selectedSimulationId}`
      )
      .then((response) => {
        setData(response.data);
        setLoading(false);
        setInitialLoading(false);
      })
      .catch((err) => {
        console.error('Błąd ładowania podsumowania MŚ:', err);
        setError('Nie udało się pobrać danych podsumowania.');
        setLoading(false);
        setInitialLoading(false);
      });
  }, [selectedSimulationId]);
  
  // Pobieranie danych dla DRUGIEJ symulacji (analogicznie do pierwszej)
useEffect(() => {
  if (!compareEnabled || !secondSimulationId) {
    setSecondSimData([]);
    return;
  }

  // UWAGA: Podmień poniższy URL na taki sam, jaki masz w useEffect dla pierwszej symulacji
  axios
      .get(
        `${process.env.REACT_APP_API_URL}/api/simulation/worldcup/latest-stages?simulationId=${secondSimulationId}`
      )
    .then(response => {
      // Dostosuj 'res.data' pod strukturę Twojego API (np. res.data.teams jeśli tam są schowane)
      setSecondSimData(response.data); 
    })
    .catch(err => console.error("Błąd pobierania drugiej symulacji:", err));
}, [secondSimulationId, compareEnabled]);
  
  const currentSimulationIndex = simulations.findIndex(
	  sim => sim.simulation_id === selectedSimulationId
	);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  const handleSimulationSelect = (event) => {
	  const simulationId = event.target.value;

	  const index = simulations.findIndex(
		sim => sim.simulation_id === simulationId
	  );

	  //setCurrentSimulationIndex(index);
	  setSelectedSimulationId(simulationId);
	};
	
	const handleSecondSimulationSelect = (event) => {
	  const simulationId = event.target.value;

	  const index = simulations.findIndex(
		sim => sim.simulation_id === simulationId
	  );

	  //setCurrentSimulationIndex(index);
	  setSecondSimulationId(simulationId);
	};

	const handlePreviousSimulation = () => {
	  if (currentSimulationIndex < simulations.length - 1) {
		const newIndex = currentSimulationIndex + 1;

		//setCurrentSimulationIndex(newIndex);
		setSelectedSimulationId(
		  simulations[newIndex].simulation_id
		);
	  }
	};

	const handleNextSimulation = () => {
	  if (currentSimulationIndex > 0) {
		const newIndex = currentSimulationIndex - 1;

		//setCurrentSimulationIndex(newIndex);
		setSelectedSimulationId(
		  simulations[newIndex].simulation_id
		);
	  }
	};	
	
const handleSecondPrev = () => {
  const currentIndex = simulations.findIndex(s => s.simulation_id === secondSimulationId);
  if (currentIndex > 0) {
    setSecondSimulationId(simulations[currentIndex - 1].simulation_id);
  }
};

const handleSecondNext = () => {
  const currentIndex = simulations.findIndex(s => s.simulation_id === secondSimulationId);
  if (currentIndex < simulations.length - 1) {
    setSecondSimulationId(simulations[currentIndex + 1].simulation_id);
  }
};

const getDiffGradientColor = (value, maxAbsValue) => {
  // Jeśli wartość wynosi 0, zwracamy kolor bazowy
  if (value === 0 || !value || !maxAbsValue) return 'rgba(255, 255, 255, 0.3)';

  // Obliczamy stopień nasycenia (0 do 1)
  const ratio = Math.min(1, Math.abs(value) / (0.9*maxAbsValue));
  
  // Parametry kolorów:
  // Neutralny: biały/szary (255, 255, 255)
  // Zielony: (76, 175, 80)
  // Czerwony: (244, 67, 54)
  
  let r, g, b;
  const alpha = 0.3 + (ratio * 0.7); // Alfa płynnie od 0.1 do 0.6

  if (value > 0) {
    // Interpolacja w stronę zieleni
    r = Math.round(255 + (76 - 255) * ratio);
    g = Math.round(255 + (255 - 255) * ratio);
    b = Math.round(255 + (80 - 255) * ratio);
  } else {
    // Interpolacja w stronę czerwieni
    r = Math.round(255 + (255 - 255) * ratio);
    g = Math.round(255 + (67 - 255) * ratio);
    b = Math.round(255 + (54 - 255) * ratio);
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
};

// Funkcja pomocnicza do formatowania różnic (dodaje znak + lub -)
const formatDiffChance = (val) => {
  if (val === undefined || val === null) return '-';
  if (val === 0) return '0.0%';
  const formatted = (val * 100).toFixed(1) + '%';
  return val > 0 ? `+${formatted}` : formatted;
};

// Funkcja pomocnicza do tooltipa (wartości czyste)
const formatAbsoluteChance = (val) => {
  if (val === undefined || val === null) return '0.0%';
  return formatChance(val);
};

// Logika sortowania tablicy wyników w locie przed renderowaniem
  const sortedResults = useMemo(() => {
  if (!data || !data.results) return [];
  
  return [...data.results].sort((a, b) => {
    const valueA = a[orderBy] ?? 0;
    const valueB = b[orderBy] ?? 0;

    if (valueB < valueA) {
      return order === 'desc' ? -1 : 1;
    }
    if (valueB > valueA) {
      return order === 'desc' ? 1 : -1;
    }
    return 0;
  });
}, [data, orderBy, order]);

// Obliczanie różnic 
const { diffTableData, maxAbsZValue } = useMemo(() => {
  const primaryData = data?.results || []; 
  const secondaryData = secondSimData?.results || [];

  if (!compareEnabled || primaryData.length === 0 || secondaryData.length === 0) {
    return { diffTableData: [], maxAbsZValue: 0 };
  }

  const secondMap = new Map(secondaryData.map(item => [item.teamId, item]));
  let globalMaxAbsZ = 0;
  const N = 10000; // Liczba symulacji (stała do testu istotności proporcji)

  const tableData = sortedResults.map(primaryRow => {
    const secondRow = secondMap.get(primaryRow.teamId) || {};
    const stages = ['pct1_16', 'pct1_8', 'pct1_4', 'pct1_2', 'pctFinal', 'pctWinner'];

    const stageProbabilities = stages.map(stage => {
      const pVal = primaryRow[stage] || 0; // p1
      const sVal = secondRow[stage] || 0; // p2
      const diff = pVal - sVal;

      // ---- OBLICZANIE STATYSTYKI Z-SCORE ----
      const variance1 = (pVal * (1 - pVal)) / N;
      const variance2 = (sVal * (1 - sVal)) / N;
      const standardError = Math.sqrt(variance1 + variance2);
      
      // Zabezpieczenie przed dzieleniem przez 0 (gdy obie szanse wynoszą 0% lub 100%)
      const zScore = standardError === 0 ? 0 : diff / standardError;	  

      // Szukamy maksymalnego Z-score w całej tabeli do sterowania gradientem
      const absZ = Math.abs(zScore);
      if (absZ > globalMaxAbsZ) {
        globalMaxAbsZ = absZ;
		if (globalMaxAbsZ > 100)
		{
			globalMaxAbsZ = 100;
		}
		if (globalMaxAbsZ < 20) {
            globalMaxAbsZ = 20;
         }
      }

      return {
        diffValue: diff,
        primaryValue: pVal,
        secondaryValue: sVal,
        zValue: zScore
      };
    });

    return {
      teamId: primaryRow.teamId,
      teamName: primaryRow.teamName,
      stageProbabilities
    };
  });

  return { 
    diffTableData: tableData, 
    maxAbsZValue: globalMaxAbsZ === 0 ? 1 : globalMaxAbsZ 
  };
}, [compareEnabled, data, secondSimData, sortedResults]);

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Typography variant="h6" color="text.secondary">Ładowanie podsumowania symulacji...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  const simulationDateTime = data?.simulationDateTime;
  const teams = data?.teams || [];
  const selectedSim = simulations.find(s => s.simulation_id === selectedSimulationId);

  
// Konfiguracja kolumn pucharowych z kluczami odpowiadającymi polom w JSON
  const columns = [
    { id: 'pct1_16', label: '1/16 Finału' },
    { id: 'pct1_8', label: '1/8 Finału' },
    { id: 'pct1_4', label: '1/4 Finału' },
    { id: 'pct1_2', label: '1/2 Finału' },
    { id: 'pctFinal', label: 'Finał' },
    { id: 'pctWinner', label: 'Zwycięzca' }
  ];

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 } }}>
      <Helmet>
        <title>Podsumowanie szans - Mistrzostwa Świata | Symuliga</title>
      </Helmet>

      {/* Tytuł nagłówka */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Mistrzostwa Świata - Szanse w fazie pucharowej
        </Typography>
        {simulations.length > 0 && (
		   <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
            <IconButton
              onClick={handlePreviousSimulation}
              disabled={currentSimulationIndex >= simulations.length - 1}
              size="small"
            >
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
			  <CustomSelect
				  value={selectedSimulationId}
				  onChange={handleSimulationSelect}
				  options={simulationOptions}
				/>
			<IconButton
              onClick={handleNextSimulation}
              disabled={currentSimulationIndex === 0}
              size="small"
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
		)}
      </Box>

		{simulations[currentSimulationIndex]?.conditions !== "" && (
            <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, textAlign: 'center' }}>
              Symulacja warunkowa!<br />
              <span dangerouslySetInnerHTML={{ __html: simulations[currentSimulationIndex]?.conditions?.replace(/\n/g, '<br />') || '' }} />
            </Typography>
          )}
      {/* Kontener tabeli (maksymalnie 1000px, wyśrodkowany, ze wszystkimi stylami graficznymi) */}
      <Box sx={{ maxWidth: '1000px', margin: '0 auto' }}>
        <TableContainer
          component={Paper}
          sx={{
            marginBottom: 1,
            margin: '0 auto',
            width: 'auto',
            maxWidth: 'fit-content',
            overflowX: 'auto',
            position: 'relative',
            borderRadius: '12px',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '4px' },
            '&::before': {
              content: '"SymuLiga.pl"',
              position: 'absolute',
              top: '60%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
              fontWeight: 'bold',
              color: 'rgba(0, 0, 0, 0.15)',
              pointerEvents: 'none',
              zIndex: 0,
              whiteSpace: 'nowrap',
            },
          }}
        >
          <Table size="small" sx={{ width: 'auto', tableLayout: 'auto', borderCollapse: 'collapse' }} aria-label="world cup knockout stages probability table">
            <TableHead>
              <TableRow>
                {/* Niesortowalna kolumna z nazwą drużyny */}
                <TableCell
                  sx={{
                    position: 'sticky',
					textAlign: 'right',
                    left: 0,
                    top: 0,
                    zIndex: 3,
                    backgroundColor: 'background.paper',
                    width: 'auto',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    padding: '6px',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                >
                  Drużyna
                </TableCell>
                
                {/* Dynamiczne i sortowalne nagłówki etapów pucharowych */}
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align="center"
                    sortDirection={orderBy === col.id ? order : false}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: 'background.paper',
                      width: 'auto',
                      padding: '6px',
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      borderBottom: '2px solid rgba(224, 224, 224, 1)',
                      fontWeight: 'bold'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'desc'}
                      onClick={() => handleRequestSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
				
			
              {sortedResults.map((row, rowIndex) => {
				  const stageProbabilities = [
							  row.pct1_16,
							  row.pct1_8,
							  row.pct1_4,
							  row.pct1_2,
							  row.pctFinal,
							  row.pctWinner
							];
                return (
                  <TableRow key={rowIndex}>
                    {/* Nazwa drużyny jako link */}
                    <TableCell
                      
                      sx={{
							  position: 'sticky',
							  textAlign: 'right',
							  left: 0,
							  zIndex: 3,
							  backgroundColor: 'background.paper',
							  width: 'auto', // Dopasowanie do zawartości
							  minWidth: 40, // Minimalna szerokość dostosowana
							  padding: '5px',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  borderRight: '1px solid rgba(224, 224, 224, 1)',
							}}
                    >
                      {row.teamName}
                    </TableCell>
					
                    {/* Komórki szans z oryginalnymi paddingami, cieniem i efektem powiększenia przy hoverze */}
                    {stageProbabilities.map((prob, colIndex) => (
                      <TableCell
                        key={colIndex}
                        align="center"
                        sx={{
								width: 'auto', // Proporcjonalne dostosowanie
								minWidth: 70, // Minimalna szerokość dostosowana
								backgroundColor: getGradientColor(prob),
								color: 'black', // Zawsze czarny dla awansu
								fontWeight: 'bold',
								fontSize: { xs: '0.8rem', sm: '0.9rem' },
								padding: '5px',
								textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
								transition: 'background-color 0.3s',
								'@media (hover: hover)': {
								  '&:hover': {
									backgroundColor: getGradientColor(Math.min(prob + 0.1, 1)),
									transform: 'scale(1.05)',
								  },
								},
							  }}
                      >
						{formatChance(prob, false)}                      
					</TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
	  
{/* ================= SEKCJA PORÓWNANIA SYMULACJI ================= */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'center' }}>
          <input
            type="checkbox"
            id="compare-checkbox"
            checked={compareEnabled}
            onChange={(e) => {
              setCompareEnabled(e.target.checked);
              if (e.target.checked && !secondSimulationId && simulations?.length > 1) {
				// Wybierz przedostatnią symulację (indeks 1, jeśli lista jest posortowana od najnowszych)
				setSecondSimulationId(simulations[1].simulation_id); 
			  } else if (e.target.checked && !secondSimulationId && simulations?.length === 1) {
				// Jeśli jest tylko jedna symulacja, wybierz ją
				setSecondSimulationId(simulations[0].simulation_id);
			  }
            }}
            style={{ transform: 'scale(1.3)', marginRight: '10px', cursor: 'pointer' }}
          />
          <label htmlFor="compare-checkbox" style={{ color: '#ffffff', fontSize: '1.1rem', cursor: 'pointer', userSelect: 'none' }}>
            Porównaj z inną symulacją
          </label>
        </Box>

        {compareEnabled && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mb: 3 }}>
              <IconButton
                onClick={handleSecondPrev}
                disabled={simulations.findIndex(s => s.simulation_id === secondSimulationId) <= 0}
                sx={{ color: '#ffffff', '&:disabled': { color: '#555555' } }}
              >
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>
              
              <CustomSelect
                value={secondSimulationId}
                onChange={handleSecondSimulationSelect}
                options={simulationOptions}
              />
              
              <IconButton
                onClick={handleSecondNext}
                disabled={simulations.findIndex(s => s.simulation_id === secondSimulationId) >= simulations.length - 1}
                sx={{ color: '#ffffff', '&:disabled': { color: '#555555' } }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>

            {diffTableData.length > 0 ? (
              <Box sx={{ maxWidth: '1000px', margin: '0 auto' }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    marginBottom: 1,
                    margin: '0 auto',
                    width: 'auto',
                    maxWidth: 'fit-content',
                    overflowX: 'auto',
                    position: 'relative',
                    borderRadius: '12px',
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '4px' },
                  }}
                >
                  <Table size="small" sx={{ width: 'auto', tableLayout: 'auto', borderCollapse: 'collapse' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            position: 'sticky',
                            textAlign: 'right',
                            left: 0,
                            top: 0,
                            zIndex: 3,
                            backgroundColor: '#2c2c2c',
                            color: '#ffffff',
                            width: 'auto',
                            maxWidth: '100%',
                            whiteSpace: 'normal',
                            padding: '6px',
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            borderRight: '1px solid rgba(224, 224, 224, 1)',
                          }}
                        >
                          Drużyna
                        </TableCell>
                        
                        {columns.map((col) => (
                          <TableCell
                            key={`diff-${col.id}`}
                            align="center"
                            sx={{
                              position: 'sticky',
                              top: 0,
                              zIndex: 2,
                              backgroundColor: '#2c2c2c',
                              color: '#ffffff',
                              width: 'auto',
                              padding: '6px',
                              fontSize: { xs: '0.8rem', sm: '0.9rem' },
                              borderBottom: '2px solid rgba(224, 224, 224, 1)',
                              fontWeight: 'bold'
                            }}
                          >
                            {col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {diffTableData.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          <TableCell
                            sx={{
                              position: 'sticky',
                              textAlign: 'right',
                              left: 0,
                              zIndex: 3,
                              backgroundColor: 'background.paper',
                              width: 'auto',
                              minWidth: 40,
                              padding: '5px',
                              fontSize: { xs: '0.8rem', sm: '0.9rem' },
                              borderRight: '1px solid rgba(224, 224, 224, 1)',
                            }}
                          >
                            {row.teamName}
                          </TableCell>
                          
                          {/* Mapowanie różnic z wygenerowanej tablicy */}
                          {row.stageProbabilities.map((probObj, colIndex) => (
                            <TableCell
                              key={`diff-cell-${colIndex}`}
                              align="center"
                              title={`${formatAbsoluteChance(probObj.secondaryValue)} --> ${formatAbsoluteChance(probObj.primaryValue)}`}
							  
                              sx={{
                                width: 'auto',
                                minWidth: 70,
                                backgroundColor: getDiffGradientColor(probObj.zValue, maxAbsZValue),
                                color: 'black', 
                                fontWeight: 'bold',
                                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                padding: '5px',
                                //textShadow: '0.5px 0.5px 1px rgba(255, 255, 255, 0.3)',
                                transition: 'background-color 0.3s',
                                cursor: 'help', // Zmiana kursora sugerująca dostępność podpowiedzi
                              }}
                            >
                              {formatDiffChance(probObj.diffValue)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Typography sx={{ color: '#aaaaaa', textAlign: 'center', mt: 2 }}>
                Wybierz drugą symulację, aby zobaczyć porównanie różnic.
              </Typography>
            )}
          </Box>
        )}
      </Box>
      {/* ================= KONIEC SEKCJI PORÓWNANIA ================= */}
    </Box>
  );
};

export default WcSummary;